/**
 * Reglas del envío de reportes por correo (US-2.8).
 *
 * El contrato exige entre 1 y 10 destinatarios con formato de email y un asunto
 * de hasta 200 caracteres; si falta el asunto, el backend usa el automático.
 */
import type { ReportFormat } from '@goelectric/shared-types';

/** Tope de destinatarios del contrato (`destinatarios` 1..10). */
export const MAX_DESTINATARIOS = 10;

/** Tope de caracteres del asunto (`asunto` maxLength 200). */
export const MAX_ASUNTO_LENGTH = 200;

/** Formato de archivo: el correo nunca manda la vista previa `json`. */
export type FileReportFormat = Exclude<ReportFormat, 'json'>;

/** Datos que emite el modal de correo. */
export interface ReportEmailValues {
  destinatarios: string[];
  formato: FileReportFormat;
  asunto: string;
}

/** Suficiente para descartar errores de tipeo; el backend valida de nuevo. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Recorta y descarta vacíos; conserva el orden de carga y quita repetidos. */
export function normalizeDestinatarios(values: readonly string[] | undefined): string[] {
  const unique = new Set<string>();
  for (const value of values ?? []) {
    const email = value.trim();
    if (email) unique.add(email);
  }
  return [...unique];
}

/**
 * Mensaje de error de los destinatarios, o `null` si son válidos. Se usa tanto
 * en la regla del formulario como en los tests.
 */
export function validateDestinatarios(values: readonly string[] | undefined): string | null {
  const emails = normalizeDestinatarios(values);
  if (emails.length === 0) return 'Agregá al menos un destinatario.';
  if (emails.length > MAX_DESTINATARIOS) {
    return `Podés enviar a ${MAX_DESTINATARIOS} destinatarios como máximo.`;
  }
  const invalidos = emails.filter((email) => !EMAIL_RE.test(email));
  if (invalidos.length === 0) return null;
  return invalidos.length === 1
    ? `Revisá este correo: ${invalidos[0]}.`
    : `Revisá estos correos: ${invalidos.join(', ')}.`;
}

/**
 * Asunto automático: `Reporte {título} — {fecha}` con la fecha en es-CO, la
 * misma idea que aplica el worker cuando no recibe `asunto`.
 */
export function defaultEmailSubject(titulo: string, date: Date = new Date()): string {
  const fecha = date.toLocaleDateString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `Reporte ${titulo} — ${fecha}`;
}

/** Destinatarios compactos para el panel: los primeros y un `+N`. */
export function formatDestinatarios(
  destinatarios: readonly string[] | undefined,
  max = 2,
): string {
  const lista = destinatarios ?? [];
  if (lista.length === 0) return '—';
  if (lista.length <= max) return lista.join(', ');
  return `${lista.slice(0, max).join(', ')} +${lista.length - max}`;
}
